/**
 * 全局项目上下文（S06 起接真实接口）。
 *
 * 项目列表来自 GET /api/projects；切换结果存 localStorage。
 * 登录前（auth 未就绪）不请求；首次加载用「记住的 id」兜底，列表到了再校正。
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { callApi } from "./api";
import { useAuth } from "./auth";

export interface ProjectOption {
  id: string;
  name: string;
  domain?: string | null;
  description?: string | null;
}

const STORAGE_KEY = "atc.currentProjectId";

interface ProjectContextValue {
  projects: ProjectOption[];
  projectId: string | null;
  projectLoading: boolean;
  switchProject: (id: string) => void;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [projectId, setProjectId] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY) || null
  );

  // S35 前单租户，无额外参数
  const q = useQuery({
    queryKey: ["projects"],
    queryFn: async () => (await callApi<{ items: ProjectOption[] }>("/api/projects")).data,
    enabled: status === "authed",
    staleTime: 60_000,
  });

  const projects = q.data?.items ?? [];

  // 首次加载：没有记住的 id 或 id 已失效时，落到第一个项目
  useEffect(() => {
    if (projects.length === 0) return;
    if (!projectId || !projects.some((p) => p.id === projectId)) {
      setProjectId(projects[0]!.id);
    }
  }, [projects, projectId]);

  useEffect(() => {
    if (projectId) localStorage.setItem(STORAGE_KEY, projectId);
  }, [projectId]);

  const value = useMemo<ProjectContextValue>(
    () => ({
      projects,
      projectId,
      projectLoading: q.isLoading,
      switchProject: setProjectId,
    }),
    [projects, projectId, q.isLoading]
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error("useProject 必须在 ProjectProvider 内使用");
  return ctx;
}
